<template>
    <div>
        <div class="provider-table">
            <div class="provider-table--controls">
                <div class="search-network-grid">
                    <div :class="{ 'toggle-view-icon': true, active: viewMode === 'grid' }" @click="viewMode = 'grid'">
                        <common-icon name="grid" size="large"></common-icon>
                    </div>
                    <div :class="{ 'toggle-view-icon': true, active: viewMode === 'list' }" @click="viewMode = 'list'">
                        <common-icon name="list" size="large"></common-icon>
                    </div>
                </div>
            </div>

            <div class="provider-table--wrapper" :class="viewMode">
                <common-grid>
                    <template v-if="practiceType === PracticeTypeEnum.PHYSICIAN">
                        <common-grid-column v-for="(element, index) in physicianConnections" :key="index">
                            <practice-member-card
                                :physician="element.user"
                                :ehr="true"
                                :useExternalReferHandler="true"
                                :insurancePlanId="patientInsuranceId"
                                :isUserProfileLinkEnabled="false"
                                :showNetworkInclusionStatus="false"
                                :showCardAvailability="false"
                                :isStandalone="true"
                                :showEmailButton="false"
                                :showPhoneButton="false"
                                :viewType="viewMode"
                                @refer="checkAvailabiliy(element)"
                            ></practice-member-card>
                        </common-grid-column>
                    </template>
                    <template v-else>
                        <common-grid-column v-for="(element, index) in ancillaryConnections" :key="index">
                            <practice-member-card
                                :physician="element.ancillary"
                                :ehr="true"
                                :useExternalReferHandler="true"
                                :insurancePlanId="patientInsuranceId"
                                :isUserProfileLinkEnabled="false"
                                :showNetworkInclusionStatus="false"
                                :showCardAvailability="false"
                                :isStandalone="true"
                                :showEmailButton="false"
                                :showPhoneButton="false"
                                :viewType="viewMode"
                                @refer="checkAvailabiliy(element)"
                            ></practice-member-card>
                        </common-grid-column>
                    </template>
                </common-grid>
            </div>
        </div>

        <direct-referral
            v-if="isDirectReferralModalOpen"
            :ehr="true"
            :user="userData"
            :activePracticeType="practiceType"
            :insurancePlanId="patientInsuranceId"
            @close="closeDirectReferralModal"
            @emitEvent="onDirectReferralEmit"
        ></direct-referral>

        <common-button
            v-show="showLoadMoreButton"
            class="provider-table__footer"
            :isProcessing="isSending"
            :label="$t('pages.network.ehr.table.view_more')"
            type="primary"
            size="large"
            @click="loadMore"
        ></common-button>
    </div>
</template>

<script>
    import PhysiciansAPI from 'api/PhysiciansAPI'
    import FacilityServicesAPI from 'api/FacilityServicesAPI'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import PracticeMemberCard from 'components/page-elements/practice/PracticeMemberCard'
    import DirectReferral from 'components/page-elements/referrals/DirectReferral'

    export default {
        mixins: [],

        components: { PracticeMemberCard, DirectReferral },

        props: {
            practiceType: { type: String, required: true },
            filters: { type: Object, required: false, default: null },
        },

        data() {
            return {
                viewMode: 'grid',
                PracticeTypeEnum: PracticeTypeEnum,
                pageSize: 0,
                physicianConnections: [],
                ancillaryConnections: [],
                physicianFilter: {
                    limit: this.pageSize,
                    offset: 0,
                    orderBy: 'DateCreatedDESC',
                },
                ancillaryFilter: {
                    limit: this.pageSize,
                    offset: 0,
                    orderBy: 'DateCreatedDESC',
                },
                insuranceId: undefined,
                specialityId: undefined,
                zipCode: undefined,
                totalCount: 0,
                isDirectReferralModalOpen: false,
                userData: null,
                showLoadMoreButton: false,
                isSending: false,
                patientInsuranceId: null,
            }
        },

        methods: {
            fetchData(append) {
                const isPhysician = this.practiceType === PracticeTypeEnum.PHYSICIAN
                const filterString = isPhysician ? 'physicianFilter' : 'ancillaryFilter'
                const zipRaw = this.filters?.zipCode
                const zip = zipRaw != null ? String(zipRaw).trim() : ''
                // Same endpoint as referral match flow: GET unauthenticated/referrals/matches (requires zip when searchBy=zipcode)
                if (isPhysician && (!zip || zip.length < 5)) {
                    this.isSending = false
                    if (!append) {
                        this.totalCount = 0
                        this.physicianConnections = []
                    }
                    return Promise.resolve()
                }

                this.isSending = true
                const request = {
                    practiceType: isPhysician ? 'physician' : 'ancillary',
                    limit: this[filterString].limit || 5,
                    offset: this[filterString].offset,
                    insurancePlanId: this.filters?.insuranceId || undefined,
                    specialityId: this.filters?.specialityId || undefined,
                    zip: isPhysician ? zip : this.filters?.zipCode || undefined,
                    searchBy: 'zipcode',
                    searchByTimeWindow: this.filters?.searchByTimeWindow || 'asap',
                }

                const apiCall = isPhysician ? PhysiciansAPI.getMatchesList(request) : FacilityServicesAPI.getMatchesList(request)

                return apiCall
                    .then((results) => {
                        this.totalCount = Number(results._headers['x-total-count'])
                        // API may use `providers` or `users`; empty `[]` is truthy — fall through to `users`.
                        const rows =
                            (Array.isArray(results.providers) && results.providers.length ? results.providers : null) ||
                            results.users ||
                            []
                        const formattedProviders = rows.map((p) => {
                            if (isPhysician) {
                                const userId = p.user?.id ?? p.userId ?? p.id
                                const user = { ...p, id: userId }
                                user.name = `${user.firstName} ${user.lastName}`
                                user.isRegistrationComplete = user.isSignupFinished
                                return { user, target: user }
                            } else {
                                p.isRegistrationComplete = true
                                return { ancillary: p, target: p }
                            }
                        })
                        this.setFetchedData(formattedProviders, append)
                    })
                    .catch(() => {
                        if (!append) {
                            this.totalCount = 0
                            if (isPhysician) {
                                this.physicianConnections = []
                            } else {
                                this.ancillaryConnections = []
                            }
                        }
                    })
                    .finally(() => {
                        this.isSending = false
                    })
            },

            setFetchedData(conns, append) {
                if (append) {
                    let collection =
                        this.practiceType === PracticeTypeEnum.PHYSICIAN ? this.physicianConnections : this.ancillaryConnections
                    for (let i = 0; i < conns.length; ++i) {
                        collection.push(conns[i])
                    }
                } else {
                    if (this.practiceType === PracticeTypeEnum.PHYSICIAN) this.physicianConnections = conns
                    else this.ancillaryConnections = conns
                }
            },

            checkAvailabiliy(element) {
                this.toggleDirectReferralModal(element)
            },

            toggleDirectReferralModal(element) {
                this.userData = this.practiceType === PracticeTypeEnum.PHYSICIAN ? element.user : element.ancillary
                this.isDirectReferralModalOpen = true
            },

            closeDirectReferralModal() {
                this.isDirectReferralModalOpen = false
            },

            onDirectReferralEmit({ name }) {
                if (name === 'location-selected') {
                    this.closeDirectReferralModal()
                }
            },

            getMainLocationName(locations) {
                const mainLocation = locations.find((item) => item.id === 0)
                return mainLocation.name
            },

            loadMore() {
                if (this.practiceType === PracticeTypeEnum.PHYSICIAN) {
                    this.physicianFilter.offset = this.physicianFilter.offset + this.physicianFilter.limit
                    this.fetchData(true)
                } else {
                    this.ancillaryFilter.offset = this.ancillaryFilter.offset + this.ancillaryFilter.limit
                    this.fetchData(true)
                }
            },
        },

        watch: {
            practiceType() {
                this.fetchData()
            },

            physicianConnections() {
                this.showLoadMoreButton = this.totalCount > this.physicianConnections.length
            },

            ancillaryConnections() {
                this.showLoadMoreButton = this.totalCount > this.ancillaryConnections.length
            },

            filters() {
                if (this.filters?.pageSize) {
                    this.pageSize = this.filters.pageSize
                }
                this.physicianConnections = []
                this.ancillaryConnections = []
                this.physicianFilter.limit = this.pageSize
                this.ancillaryFilter.limit = this.pageSize
                this.physicianFilter.offset = 0
                this.ancillaryFilter.offset = 0
                this.fetchData()
            },
        },

        beforeCreate() {
            // Align with `pool.new` default query limit in `src/router/routes/referrals-pool.js`
            this.pageSize = 9
        },

        mounted() {
            this.patientInsuranceId = 246
            this.fetchData()
        },
    }
</script>

<style lang="scss">
    @import 'styles/components/page-elements/ehr/_provider-table';
</style>
